import React, { useState, useEffect } from 'react';
import { logger, LogLevel } from '../services/logger';
import { apiService } from '../services/apiService';
import { VersionInfo } from './VersionInfo';
import { LOG_LEVEL_PREF_KEY } from '../types';

export const LoggingSettings: React.FC = () => {
    const [currentLevel, setCurrentLevel] = useState<LogLevel>(logger.getLevel());
    const [versions, setVersions] = useState<{ fe: any; be: any }>({ fe: null, be: null });
    const [versionsLoading, setVersionsLoading] = useState(true);

    const handleLevelChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newLevel = parseInt(e.target.value, 10) as LogLevel;
        logger.setLevel(newLevel);
        setCurrentLevel(newLevel);
        apiService.setPreference(LOG_LEVEL_PREF_KEY, newLevel).catch(err => {
            logger.error("Failed to save log level preference to DB", err);
        });
    };
    
    useEffect(() => {
        const fetchVersions = async () => {
            setVersionsLoading(true);
            
            const fePromise = fetch('/version.json').then(res => {
                if (res.ok) return res.json();
                throw new Error(`Frontend version file not found (${res.status})`);
            });

            const bePromise = apiService.getBackendVersion();
            
            const results = await Promise.allSettled([fePromise, bePromise]);

            const feResult = results[0];
            const beResult = results[1];

            const feData = feResult.status === 'fulfilled' 
                ? feResult.value 
                : { version: 'N/A', commit: 'error', error: 'Nepodařilo se načíst' };
            
            const beData = beResult.status === 'fulfilled' 
                ? beResult.value 
                : { version: 'N/A', commit: 'error', error: 'Nepodařilo se načíst' };
            
            if(feResult.status === 'rejected') console.error("Chyba při načítání FE verze:", feResult.reason);
            if(beResult.status === 'rejected') console.error("Chyba při načítání BE verze:", beResult.reason);

            setVersions({ fe: feData, be: beData });
            setVersionsLoading(false);
        };
        fetchVersions();
    }, []);

    const levels = [
        { value: LogLevel.NONE, label: 'Žádné' },
        { value: LogLevel.ERROR, label: 'Pouze chyby (Error)' },
        { value: LogLevel.WARN, label: 'Varování (Warn)' },
        { value: LogLevel.INFO, label: 'Informační (Info)' },
        { value: LogLevel.DEBUG, label: 'Ladící (Debug)' },
    ];

    return (
        <div className="space-y-8">
            <div className="bg-white p-6 rounded-lg shadow">
                <h3 className="text-xl font-semibold text-gray-700 mb-4">Úroveň logování v konzoli prohlížeče</h3>
                <p className="text-sm text-gray-600 mb-6">
                    Vyberte, jaké množství informací se má zobrazovat v developer konzoli vašeho prohlížeče.
                    Vyšší úroveň zahrnuje všechny úrovně pod ní. Pro běžný provoz se doporučuje 'Informační' nebo 'Varování'.
                </p>
                <div className="max-w-xs">
                    <label htmlFor="log-level-select" className="block text-sm font-medium text-gray-700 mb-2">
                        Aktuální úroveň:
                    </label>
                    <select
                        id="log-level-select"
                        value={currentLevel}
                        onChange={handleLevelChange}
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                        {levels.map(level => (
                            <option key={level.value} value={level.value}>
                                {level.label}
                            </option>
                        ))}
                    </select>
                </div>
            </div>
            <VersionInfo isLoading={versionsLoading} versions={versions} />
        </div>
    );
};